import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {AiChatAccessState} from '../models/ai-chat-access.model';
import {AiChatSessionStateStore} from '../state/ai-chat-session-state.store';
import {AiChatModule} from '../ai-chat.module';
import {getCurrentUserId, getSessionSignature} from './ai-chat-auth-session';

const ACCESS_URL = '/core/ai-chat/access.json';

@Injectable({
  providedIn: 'root',
})
export class AiChatAccessService {
  constructor(
    private readonly http: HttpClient,
    private readonly stateStore: AiChatSessionStateStore,
  ) {}

  ensureAccess(): Observable<AiChatAccessState> {
    const currentSessionSignature = getSessionSignature();
    const persistedState = this.stateStore.getState();

    if (
      persistedState?.sessionSignature &&
      persistedState.sessionSignature !== currentSessionSignature
    ) {
      this.stateStore.clear();
    }

    if (!getCurrentUserId()) {
      this.stateStore.clear();
      return this.http
        .get<AiChatAccessState>(ACCESS_URL, {headers: AiChatModule.NO_CACHE_HEADERS})
        .pipe(
          tap((access) => {
            if (access.enabled) {
              this.stateStore.patchState({access});
            }
          }),
          catchError(() => of(this.disabledAccess())),
        );
    }

    const cachedAccess = this.stateStore.getState()?.access;
    if (cachedAccess && typeof cachedAccess.enabled === 'boolean') {
      return of(cachedAccess);
    }

    return this.http
      .get<AiChatAccessState>(ACCESS_URL, {headers: AiChatModule.NO_CACHE_HEADERS})
      .pipe(
        tap((access) => this.stateStore.patchState({
          access,
          sessionSignature: currentSessionSignature,
        })),
        catchError(() => of(this.disabledAccess())),
      );
  }

  private disabledAccess(): AiChatAccessState {
    return {
      enabled: false,
      aiChatEnabled: false,
      canUseAiChat: false,
    };
  }
}
